import React, { Fragment, useEffect } from "react";
import PropTypes from "prop-types";
import {
  useTable,
  useGlobalFilter,
  useAsyncDebounce,
  useSortBy,
  useFilters,
  useExpanded,
  usePagination,
  useRowSelect,
} from "react-table";
import { Table, Row, Col, Button, Input, CardBody } from "reactstrap";
import { Filter, DefaultColumnFilter } from "./filters";

import { ItemsPerPage } from "./ItemsPerPage";

import "./tableContainer.styles.scss";

// Define a default UI for filtering

const TableContainer = ({
  columns,
  data,
  customPageSize,
  tableClass,
  theadClass,
  trClass,
  thClass,
  divClass,
  individualRow,
  renderRowSubComponent, // optional: (row) => ReactNode rendered as an expandable sub-row
  initialSortBy = [{ id: "Id", desc: true }], // <-- Default fallback
  // Task 24771 - optional (rows) => void, called with the ORIGINAL row objects
  // currently on screen. A host with a select-all needs to tell "the 25 you
  // can see" from "all 6,544 that match the filter"; only this component
  // knows which slice is showing.
  onVisibleRowsChange,
}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    visibleColumns,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    // Task 24740 - `rows` is every row after filtering, `page` is only the
    // slice on screen. The count has to come from `rows`, not `data`, or a
    // filtered table would claim a total it is not showing.
    rows,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      defaultColumn: { Filter: DefaultColumnFilter },
      initialState: {
        pageIndex: 0,
        pageSize: customPageSize,
        selectedRowIds: 0,
        sortBy: initialSortBy,
      },
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect
  );
  const generateSortingIndicator = (column) => {
    return column.isSorted ? (column.isSortedDesc ? " " : "") : "";
  };

  // Task 24771 - report the rows actually on screen.
  //
  // Keyed on the ids rather than on `page` itself: react-table rebuilds that
  // array every render, so depending on it would call back on every render,
  // and any host that puts the result in state would loop forever. The ids
  // change only when the page, the page size, the sort or the filter change,
  // which is exactly when a host needs to know.
  const visibleRowKey = page
    .map((row) => row.original?.Id ?? row.id)
    .join(",");

  useEffect(() => {
    if (!onVisibleRowsChange) return;
    onVisibleRowsChange(page.map((row) => row.original));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleRowKey]);

  // useEffect(() => {
  //   setPageSize(customPageSize);
  // }, [customPageSize]);

  const onChangeInSelect = (event) => {
    setPageSize(Number(event.target.value));
  };
  const onChangeInInput = (event) => {
    const page = event.target.value ? Number(event.target.value) - 1 : 0;
    gotoPage(page);
  };

  return (
    <Fragment>
      {/* Task 24893 - the overflow rule lives in tableContainer.styles.scss
          now. An inline `overflow: visible` here cancelled `.table-responsive`
          on every list in the app, so on a phone the table ran off the right
          edge of the page instead of scrolling inside its card. */}
      <div
        className={`${divClass || ""} app-table-wrap`.trim()}
        style={{ marginTop: "5px" }}
      >
        <Table
          hover
          {...getTableProps()}
          className={tableClass}
          // style={{ minHeight: "250px" }}
        >
          <thead className={theadClass}>
            {headerGroups.map((headerGroup) => (
              <tr
                className={trClass}
                key={headerGroup.id}
                {...headerGroup.getHeaderGroupProps()}
              >
                {headerGroup.headers.map((column) => (
                  <th
                    key={column.id}
                    className={thClass}
                    {...column.getSortByToggleProps()}
                  >
                    {column.render("Header")}
                    {generateSortingIndicator(column)}
                    {/* <Filter column={column} /> */}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <Fragment key={row.getRowProps().key}>
                  <tr className={individualRow + row.getRowProps().key}>
                    {row.cells.map((cell) => {
                      return (
                        <td key={cell.id} {...cell.getCellProps()}>
                          {cell.render("Cell")}
                        </td>
                      );
                    })}
                  </tr>
                  {renderRowSubComponent && row.isExpanded && (
                    <tr>
                      <td colSpan={visibleColumns.length} className="p-0">
                        {renderRowSubComponent(row)}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </Table>
      </div>

      <Row className="justify-content-md-end justify-content-center align-items-center p-2">
        {/* Task 24740 - "not sure if this is the full list". The row count was
            available but only inside the per-page dropdown, as the label of its
            last option ("All 11") - you had to open a select to find out how
            much you were looking at. "Page 1 of 2" tells you there is more
            somewhere without saying how much, which is exactly the doubt that
            raised the task. This states it outright.

            Placed before the controls and pushed left, so it reads as a
            statement about the table rather than another button. Hidden when
            there is nothing to count. */}
        {rows.length > 0 && (
          <Col className="col-12 col-md text-muted small text-center text-md-start mb-2 mb-md-0">
            {/* pageSize can legitimately be 0 - "All" sets it to the row count,
                which is 0 while the data is still loading - so the range is
                derived from what is actually on screen rather than from
                pageIndex * pageSize, which would read "1-0 of 11". */}
            Showing {rows.length === page.length ? 1 : pageIndex * pageSize + 1}
            {"-"}
            {rows.length === page.length
              ? rows.length
              : Math.min(pageIndex * pageSize + page.length, rows.length)}{" "}
            of {rows.length}
          </Col>
        )}
        <Col className="col-md-auto">
          <ItemsPerPage
            searchResultLength={data?.length}
            setDataItemsPerPage={setPageSize}
          />
        </Col>

        <Col className="col-md-auto">
          <div className="d-flex gap-1">
            <Button
              color="primary"
              onClick={previousPage}
              disabled={!canPreviousPage}
            >
              {"<"}
            </Button>
          </div>
        </Col>
        <Col className="col-md-auto d-none d-md-block">
          Page{" "}
          <strong>
            {pageIndex + 1} of {pageOptions.length}
          </strong>
        </Col>
        <Col className="col-md-auto">
          <Input
            type="number"
            min={1}
            style={{ width: 70 }}
            max={pageOptions.length}
            defaultValue={pageIndex + 1}
            onChange={onChangeInInput}
          />
        </Col>

        <Col className="col-md-auto">
          <div className="d-flex gap-1">
            <Button color="primary" onClick={nextPage} disabled={!canNextPage}>
              {">"}
            </Button>
          </div>
        </Col>
      </Row>
    </Fragment>
  );
};

TableContainer.propTypes = {
  preGlobalFilteredRows: PropTypes.any,
};

export default TableContainer;
